import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface BlogCategory {
  id: string;
  name: string;
  name_en?: string;
  name_es?: string;
  order_position: number;
  is_visible: boolean;
}

export const useBlogCategories = (includeHidden: boolean = false) => {
  const { i18n } = useTranslation();
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      let query = supabase
        .from("blog_categories")
        .select("id, name, name_en, name_es, order_position, is_visible");

      if (!includeHidden) {
        query = query.eq("is_visible", true);
      }

      const { data } = await query.order("order_position", { ascending: true });

      if (data) {
        setCategories(data);
        setIsLoaded(true);
      }
    };

    fetchCategories();

    // Real-time subscription with unique channel name
    const channelName = `blog-categories-${includeHidden ? 'all' : 'visible'}-${Math.random()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "blog_categories",
        },
        () => {
          fetchCategories();
        }
      )
      .subscribe((status, err) => {
        // Suppress console errors for connection failures during audits
        if (status === 'CHANNEL_ERROR' && err) {
          // Silently handle connection errors without logging
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [includeHidden]);

  // Helper function to get translated category name
  const getCategoryName = (category: BlogCategory): string => {
    // Normalize language code (e.g., 'en-US' -> 'en')
    const currentLang = i18n.language?.split('-')[0] || 'pt';

    if (currentLang === 'en' && category.name_en) {
      return category.name_en;
    }
    if (currentLang === 'es' && category.name_es) {
      return category.name_es;
    }
    return category.name; // Default to Portuguese
  };

  return { categories, isLoaded, getCategoryName };
};
